import { useMemo } from 'react';
import { useI18n } from './useI18n';
import { COUNTRIES } from '../data/countries';

/**
 * A selectable country option for a Mantine `<Select>`:
 * `value` is the ISO 3166-1 alpha-2 code, `label` is the localized country name.
 */
export interface CountryOption {
  value: string;
  label: string;
}

/**
 * Builds the country options for the map configurator's country picker.
 *
 * Labels are resolved via `Intl.DisplayNames` in the current UI language
 * (falling back to the raw code if the platform cannot resolve a name).
 * Options are sorted by localized label using the same locale, and the result
 * is memoized on `currentLanguage` so it only recomputes on language change.
 */
export const useCountryOptions = (): CountryOption[] => {
  const { currentLanguage } = useI18n();

  return useMemo(() => {
    try {
      const displayNames = new Intl.DisplayNames([currentLanguage], { type: 'region' });
      const collator = new Intl.Collator(currentLanguage);

      return COUNTRIES.map((country): CountryOption => ({
        value: country.code,
        label: displayNames.of(country.code) ?? country.code,
      })).sort((first, second) => collator.compare(first.label, second.label));
    } catch {
      return [...COUNTRIES]
        .sort((countryA, countryB) => countryA.code.localeCompare(countryB.code))
        .map((country): CountryOption => ({
          value: country.code,
          label: country.code,
        }));
    }
  }, [currentLanguage]);
};
